/**
 * LoanChatPanel — the shared chat UI (header + message list + composer).
 *
 * Used by BOTH the drilldown Chat tab (fills the content area) and the floating
 * chat widget (inside a fixed-size popover). It's a self-contained flex column
 * that fills its parent — the parent decides sizing. Pass `onClose` to render a
 * close affordance in the header (the floating widget does; the tab doesn't).
 *
 * Data/transport all live in useLoanChat; this file is presentation only.
 */
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";
import IconButton from "@mui/material/IconButton";
import CloseRounded from "@mui/icons-material/CloseRounded";
import SendRounded from "@mui/icons-material/SendRounded";
import ForumOutlined from "@mui/icons-material/ForumOutlined";

import { useAuth } from "../../../../auth/AuthProvider";
import { useLoanChat } from "../../../../realtime/useLoanChat";
import { htmlStringToText } from "../../../../_src_shared/utils/functions/htmlStringToText";
import {
  formatDateTime,
  normalizeIsoStringToUtc,
} from "../../../../_src_shared/utils/functions/formatDateTime";

const MESSAGE_FZ = 12.5;
const MESSAGE_FW = 500;

const NAME_FZ = 11.5;
const NAME_FW = 400;

const DATE_FZ = 11;
const DATE_FW = 500;

const TIME_FZ = 10.5;
const TIME_FW = 300;

const CONTAINER_MAX_W = 1080; // px, matches the drilldown content area

const T = {
  bg: "#f7f9fa",
  line: "#e3e9ec",
  ink: "#0f172a",
  sub: "#64748b",
  mine: "#0a66ff",
  ok: "#0b8f1a",
  amber: "#b45309",
};

// Stable per-sender accent (for the avatar / name of other people's messages).
const ACCENTS = [
  "#0a66ff",
  "#7c3aed",
  "#0891b2",
  "#b45309",
  "#be123c",
  "#15803d",
];
const accentFor = (key) => {
  let h = 0;
  for (let i = 0; i < (key || "").length; i += 1)
    h = (h * 31 + key.charCodeAt(i)) | 0;
  return ACCENTS[Math.abs(h) % ACCENTS.length];
};

// Plain text from a chat message's htmlString, PRESERVING line breaks.
// htmlStringToText alone (DOMParser textContent) silently drops <br>/<p> breaks,
// so convert those to newlines first; the panel renders with white-space: pre-wrap.
const chatHtmlToText = (html) =>
  htmlStringToText(
    (html || "")
      .replace(/<br\s*\/?>/gi, "\n")
      .replace(/<\/p>\s*<p[^>]*>/gi, "\n\n")
      .replace(/<\/(p|div)>/gi, "\n"),
  );

const initials = (name) =>
  (name || "?")
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0]?.toUpperCase())
    .join("") || "?";

// ── Day grouping helpers (all Eastern, matching formatDateTime) ──────────────
// Consecutive messages from the same sender within this window collapse under a
// single name/avatar/time header.
const GROUP_WINDOW_MS = 5 * 60 * 1000;

const msTime = (iso) => {
  if (!iso) return 0;
  const t = new Date(normalizeIsoStringToUtc(iso)).getTime();
  return Number.isFinite(t) ? t : 0;
};
const dayKeyOf = (iso) => formatDateTime(iso, "yyyy-MM-dd");
const dayLabelOf = (iso) => {
  const key = dayKeyOf(iso);
  const today = formatDateTime(new Date().toISOString(), "yyyy-MM-dd");
  const yesterday = formatDateTime(
    new Date(Date.now() - 86400000).toISOString(),
    "yyyy-MM-dd",
  );
  if (key === today) return `Today · ${formatDateTime(iso, "MMM d, yyyy")}`;
  if (key === yesterday)
    return `Yesterday · ${formatDateTime(iso, "MMM d, yyyy")}`;
  return formatDateTime(iso, "EEEE, MMM d, yyyy");
};

// Fold a flat, oldest-first message list into day dividers + sender groups.
const buildThread = (messages, myEmail) => {
  const items = [];
  let lastDayKey = null;
  let group = null;
  for (const m of messages) {
    const dk = dayKeyOf(m.sentAt);
    if (dk !== lastDayKey) {
      items.push({ type: "day", id: `day-${dk}`, label: dayLabelOf(m.sentAt) });
      lastDayKey = dk;
      group = null;
    }
    const t = msTime(m.sentAt);
    const sameGroup =
      group &&
      group.senderEmail === m.senderEmail &&
      t - group.lastMs <= GROUP_WINDOW_MS;
    if (!sameGroup) {
      group = {
        type: "group",
        id: `g-${m.id}`,
        mine: m.senderEmail === myEmail,
        senderEmail: m.senderEmail,
        senderName: m.senderName,
        headerIso: m.sentAt,
        messages: [],
        lastMs: t,
      };
      items.push(group);
    }
    group.messages.push(m);
    group.lastMs = t;
  }
  return items;
};

function TransportPill({ transport }) {
  const map = {
    live: { label: "Live", color: T.ok },
    connecting: { label: "Connecting…", color: T.sub },
    polling: { label: "Polling", color: T.amber },
  };
  const { label, color } = map[transport] || map.polling;
  return (
    <Box
      sx={{
        display: "inline-flex",
        alignItems: "center",
        gap: 0.6,
        fontSize: 11,
        fontWeight: 700,
        color,
        background: `${color}14`,
        border: `1px solid ${color}33`,
        borderRadius: "999px",
        px: "8px",
        py: "2px",
      }}
    >
      <Box
        component="span"
        sx={{ width: 7, height: 7, borderRadius: "50%", background: color }}
      />
      {label}
    </Box>
  );
}

// Centered day separator pill ("Today · Jun 24, 2026").
function DayDivider({ label }) {
  return (
    <Box sx={{ display: "flex", justifyContent: "center", my: 0.75 }}>
      <Box
        sx={{
          fontSize: DATE_FZ,
          fontWeight: DATE_FW,
          // fontSize: 11,
          // fontWeight: 600,
          color: T.sub,
          // background: "#eef2f5",
          borderRadius: "999px",
          background: "#e5e9ec",
          px: 1.25,
          py: 0.4,
        }}
      >
        {label}
      </Box>
    </Box>
  );
}

// A single filled bubble. The first bubble in a group gets the asymmetric "tail"
// corner pointing toward its sender; the rest are fully rounded.
function MessageBubble({ html, mine, first }) {
  const text = useMemo(() => chatHtmlToText(html), [html]);
  return (
    <Box
      sx={{
        px: 1.25,
        py: 0.75,
        borderRadius: "14px",
        ...(first &&
          (mine
            ? { borderTopRightRadius: "4px" }
            : { borderTopLeftRadius: "4px" })),
        background: mine ? T.mine : "#ffffff",
        color: mine ? "#fff" : T.ink,
        border: mine ? "none" : `1px solid ${T.line}`,
        fontSize: MESSAGE_FZ,
        fontWeight: MESSAGE_FW,
        // fontSize: 13.5,
        lineHeight: 1.45,
        whiteSpace: "pre-wrap",
        wordBreak: "break-word",
        maxWidth: "100%",
        boxShadow: mine ? "none" : "0 1px 1px rgba(15,23,42,0.04)",
      }}
    >
      {text || <em style={{ opacity: 0.6 }}>(empty)</em>}
    </Box>
  );
}

// One sender group: avatar + a single name/time header, then stacked bubbles.
// Own messages (`mine`) mirror to the right with the header order reversed.
function MessageGroup({ group }) {
  const { mine, senderEmail, senderName, headerIso, messages } = group;
  const accent = mine ? T.mine : accentFor(senderEmail || senderName);
  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: mine ? "row-reverse" : "row",
        gap: 1,
        alignItems: "flex-start",
      }}
    >
      <Box
        title={senderName}
        sx={{
          flex: "0 0 auto",
          width: 28,
          height: 28,
          mt: "20px", // drop the avatar to sit beside the first bubble, not the name
          borderRadius: "50%",
          background: `${accent}1a`,
          color: accent,
          fontSize: 11,
          fontWeight: 700,

          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        {initials(senderName)}
      </Box>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          maxWidth: "78%",
          minWidth: 0,
          gap: 0.4,
          alignItems: mine ? "flex-end" : "flex-start",
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: mine ? "row-reverse" : "row",
            gap: 0.75,
            alignItems: "baseline",
            px: 0.25,
          }}
        >
          <Box
            sx={{
              //
              fontSize: NAME_FZ,
              fontWeight: NAME_FW,
              // fontSize: 11,
              // fontWeight: 500,
              color: T.ink,
            }}
          >
            {senderName}
          </Box>
          <Box
            sx={{
              //
              fontSize: TIME_FZ,
              fontWeight: TIME_FW,
              // fontSize: 10.5,
              color: T.sub,
            }}
          >
            {headerIso ? formatDateTime(headerIso, "time") : ""}
          </Box>
        </Box>
        {messages.map((m, i) => (
          <MessageBubble
            key={m.id}
            html={m.htmlString}
            mine={mine}
            first={i === 0}
          />
        ))}
      </Box>
    </Box>
  );
}

// Composer auto-grow bounds (px).
const COMPOSER_MIN_H = 66;
const COMPOSER_MAX_H = 200;

export default function LoanChatPanel({
  dealId: dealIdProp,
  onClose,
  subtitle,
  headerActions,
}) {
  const dealIdFromStore = useSelector((s) => s.loanDrilldown?._id);
  const dealId = dealIdProp || dealIdFromStore;

  const { user } = useAuth();
  const myEmail = (user?.email || "").toLowerCase();

  const { messages, loading, sending, send, transport } = useLoanChat(dealId, {
    enabled: !!dealId,
  });

  // Fold the flat list into day dividers + grouped sender blocks.
  const thread = useMemo(
    () => buildThread(messages, myEmail),
    [messages, myEmail],
  );

  const [draft, setDraft] = useState("");
  const endRef = useRef(null);
  const taRef = useRef(null);

  // Keep the view pinned to the newest message as history loads / messages arrive.
  useLayoutEffect(() => {
    endRef.current?.scrollIntoView({ block: "end" });
  }, [messages.length]);

  // Auto-grow the composer to fit its content (bounded), shrinking back when the
  // draft is cleared (e.g. after send).
  useLayoutEffect(() => {
    const el = taRef.current;
    if (!el) return;
    el.style.height = "auto";
    el.style.height = `${Math.min(
      Math.max(el.scrollHeight, COMPOSER_MIN_H),
      COMPOSER_MAX_H,
    )}px`;
  }, [draft]);

  // Reset the draft when switching loans.
  useEffect(() => {
    setDraft("");
  }, [dealId]);

  const handleSend = async () => {
    const text = draft.trim();
    if (!text || sending) return;
    setDraft("");
    try {
      await send(text);
    } catch {
      setDraft(text); // restore on failure so the message isn't lost
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  return (
    <Box
      sx={{
        flexGrow: 1,
        display: "flex",
        flexDirection: "column",
        background: T.bg,
        minHeight: 0,

        height: "100%",
      }}
    >
      {/* Header */}
      <Box
        sx={{
          flex: "0 0 auto",
          display: "flex",
          alignItems: "center",
          gap: 1,
          px: 2,
          py: 1.25,
          background: "#fff",
          borderBottom: `1px solid ${T.line}`,
        }}
      >
        <ForumOutlined sx={{ fontSize: 18, color: T.sub }} />
        <Box sx={{ fontSize: 14, fontWeight: 600, color: T.ink }}>
          Loan Chat
        </Box>
        {subtitle && (
          <Box
            sx={{
              fontSize: 12,
              color: T.sub,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {subtitle}
          </Box>
        )}
        <Box sx={{ ml: "auto", display: "flex", alignItems: "center", gap: 1 }}>
          <TransportPill transport={transport} />
          {headerActions}
          {onClose && (
            <IconButton size="small" onClick={onClose} aria-label="Close chat">
              <CloseRounded sx={{ fontSize: 18, color: T.sub }} />
            </IconButton>
          )}
        </Box>
      </Box>

      {/* Messages */}
      <Box
        sx={{
          flexGrow: 1,
          overflowY: "auto",
          minHeight: 0,
          px: 2,
          py: 2,
          display: "flex",
          flexDirection: "column",
          gap: 2.5,
          maxWidth: CONTAINER_MAX_W,
          width: "100%",
          margin: "0 auto",
          // gap: 1.25,
        }}
      >
        {loading && !messages.length ? (
          <Box
            sx={{
              flexGrow: 1,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: T.sub,
              gap: 1,
            }}
          >
            <CircularProgress size={18} />
            <Box sx={{ fontSize: 13 }}>Loading chat…</Box>
          </Box>
        ) : !messages.length ? (
          <Box
            sx={{
              flexGrow: 1,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              color: T.sub,
              gap: 0.5,
            }}
          >
            <ForumOutlined sx={{ fontSize: 32, opacity: 0.4 }} />
            <Box sx={{ fontSize: 13.5 }}>No messages yet</Box>
            <Box sx={{ fontSize: 12 }}>Start the conversation below.</Box>
          </Box>
        ) : (
          thread.map((item) =>
            item.type === "day" ? (
              <DayDivider key={item.id} label={item.label} />
            ) : (
              <MessageGroup key={item.id} group={item} />
            ),
          )
        )}
        <Box ref={endRef} />
      </Box>

      {/* Composer */}
      <Box
        sx={{
          flex: "0 0 auto",
          display: "flex",
          alignItems: "flex-end",
          gap: 1,
          px: 2,

          py: 1.5,
          background: "#fff",
          borderTop: `1px solid ${T.line}`,
        }}
      >
        <Box
          sx={{
            flex: "0 0 auto",
            display: "flex",
            alignItems: "flex-end",
            gap: 1,
            maxWidth: CONTAINER_MAX_W,
            width: "100%",
            margin: "0 auto",
          }}
        >
          <Box
            ref={taRef}
            component="textarea"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Message the team anything about this loan…  (Enter to send, Shift+Enter for a new line)"
            rows={1}
            disabled={!dealId}
            sx={{
              flexGrow: 1,
              resize: "none",
              overflowY: "auto",
              minHeight: COMPOSER_MIN_H,
              maxHeight: COMPOSER_MAX_H,
              px: 1.75,
              py: 1.25,
              fontSize: 14,
              // fontSize: 14,
              fontFamily: "inherit",
              lineHeight: 1.5,
              color: T.ink,
              border: `1px solid ${T.line}`,
              borderRadius: "12px",
              outline: "none",
              "&:focus": { borderColor: T.mine },
            }}
          />
          <IconButton
            onClick={handleSend}
            disabled={!draft.trim() || sending || !dealId}
            sx={{
              flex: "0 0 auto",
              width: 40,
              height: 40,
              background: T.mine,
              color: "#fff",
              "&:hover": { background: "#0a5ae0" },
              "&.Mui-disabled": { background: "#cbd5e1", color: "#fff" },
            }}
          >
            {sending ? (
              <CircularProgress size={18} color="inherit" />
            ) : (
              <SendRounded sx={{ fontSize: 18 }} />
            )}
          </IconButton>
        </Box>
      </Box>
    </Box>
  );
}
